import { useEffect, useState, useMemo } from "react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, UserPlus } from "lucide-react";

const STATUSES = ["New", "Contacted", "Estimate Scheduled", "Estimate Sent", "Won", "Lost"];
const SOURCES = ["Website", "Google", "Facebook", "Referral", "Door Hanger", "Walk-in", "Other"];

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = async () => { const r = await endpoints.list("leads"); setLeads(r.data); };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = Object.fromEntries(STATUSES.map(s => [s, []]));
    leads.forEach(l => { if (g[l.status]) g[l.status].push(l); });
    return g;
  }, [leads]);

  const startNew = () => {
    setEditing(null);
    setForm({ first_name: "", last_name: "", phone: "", email: "", area: "", source: "Website", status: "New", interest: "", estimated_value: 0, notes: "" });
    setOpen(true);
  };
  const startEdit = (l) => { setEditing(l); setForm(l); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await endpoints.update("leads", editing.id, form);
      else await endpoints.create("leads", form);
      toast.success("Lead saved");
      setOpen(false);
      load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await endpoints.remove("leads", id);
    toast.success("Lead deleted");
    load();
  };

  const moveStatus = async (lead, status) => {
    await endpoints.update("leads", lead.id, { ...lead, status });
    load();
  };

  const convertToClient = async (lead) => {
    const clientPayload = {
      first_name: lead.first_name, last_name: lead.last_name,
      phone: lead.phone, email: lead.email,
      preferred_contact: "Email", type: "Residential",
      neighborhood: lead.area, street: "", city: "Austin", state: "TX", zip: "",
      internal_notes: `Converted from lead — ${lead.source}.`,
    };
    await endpoints.create("clients", clientPayload);
    await endpoints.update("leads", lead.id, { ...lead, status: "Won" });
    toast.success("Lead converted to client");
    load();
  };

  return (
    <Layout title="Leads" subtitle={`${leads.length} prospects in pipeline`}>
      <div className="flex justify-end mb-4">
        <Button onClick={startNew} data-testid="leads-new"><Plus className="size-4 mr-1.5"/>New Lead</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="leads-kanban">
        {STATUSES.map((status) => (
          <div key={status} className="bg-muted/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-semibold text-sm">{status}</div>
              <span className="text-xs px-2 py-0.5 rounded-full bg-background border border-border font-mono">{grouped[status].length}</span>
            </div>
            <div className="space-y-2 min-h-[40px]">
              {grouped[status].map((l) => (
                <Card key={l.id} className="p-4 rounded-lg hover:border-primary/40 transition-colors" data-testid={`lead-card-${l.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm truncate">{l.first_name} {l.last_name}</div>
                      <div className="text-xs text-muted-foreground truncate">{l.interest}</div>
                    </div>
                    <span className="text-xs font-mono text-primary font-semibold">${l.estimated_value}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-2 flex items-center gap-2">
                    <span>{l.source}</span>·<span>{l.area}</span>
                  </div>
                  <div className="flex items-center gap-1 mt-3">
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => startEdit(l)}><Pencil className="size-3 mr-1"/>Edit</Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => convertToClient(l)}><UserPlus className="size-3 mr-1"/>Convert</Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-destructive" onClick={() => remove(l.id)}><Trash2 className="size-3"/></Button>
                  </div>
                  <div className="mt-2">
                    <Select value={l.status} onValueChange={(v) => moveStatus(l, v)}>
                      <SelectTrigger className="h-7 text-xs" data-testid={`lead-status-${l.id}`}><SelectValue/></SelectTrigger>
                      <SelectContent>
                        {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="lead-form-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit Lead" : "New Lead"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-2">
            <div className="space-y-1.5"><Label>First Name</Label><Input value={form.first_name||""} onChange={(e)=>setForm({...form, first_name:e.target.value})} data-testid="lead-first-name"/></div>
            <div className="space-y-1.5"><Label>Last Name</Label><Input value={form.last_name||""} onChange={(e)=>setForm({...form, last_name:e.target.value})} data-testid="lead-last-name"/></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone||""} onChange={(e)=>setForm({...form, phone:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Email</Label><Input value={form.email||""} onChange={(e)=>setForm({...form, email:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Area</Label><Input value={form.area||""} onChange={(e)=>setForm({...form, area:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Source</Label>
              <Select value={form.source||"Website"} onValueChange={(v)=>setForm({...form, source:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={form.status||"New"} onValueChange={(v)=>setForm({...form, status:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Est. Value ($)</Label><Input type="number" value={form.estimated_value||0} onChange={(e)=>setForm({...form, estimated_value: Number(e.target.value)})}/></div>
            <div className="space-y-1.5 col-span-2"><Label>Interest</Label><Input value={form.interest||""} onChange={(e)=>setForm({...form, interest:e.target.value})}/></div>
            <div className="space-y-1.5 col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes||""} onChange={(e)=>setForm({...form, notes:e.target.value})}/></div>
          </div>
          <DialogFooter className="mt-2"><Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="lead-save">Save Lead</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
